import type { ChatContextId } from "../../../store/aiChatStore";

export const CENTINELA_CHAT_ROUTE = "/chat-ia";

export const CHAT_SUGGESTION_KEYS = ["stopped", "offline", "speeding", "summary"] as const;

export type ChatSuggestionKey = (typeof CHAT_SUGGESTION_KEYS)[number];

export const CHAT_SUGGESTION_ICONS: Record<string, string> = {
  stopped: "ri-pause-circle-line",
  offline: "ri-signal-wifi-error-line",
  speeding: "ri-dashboard-3-line",
  summary: "ri-file-list-3-line",
  longStop: "ri-time-line",
  nightDriving: "ri-moon-line",
  engineOff: "ri-settings-3-line",
  offRoute: "ri-route-line",
  lowFuel: "ri-gas-station-line",
  activeAlerts: "ri-error-warning-line",
  mapFocus: "ri-focus-3-line",
  mapRoutes: "ri-route-line",
  mapRisk: "ri-alarm-warning-line",
  mapFuel: "ri-gas-station-line",
  reportDaily: "ri-calendar-check-line",
  reportCalls: "ri-phone-line",
  reportKm: "ri-road-map-line",
  reportAlerts: "ri-error-warning-line",
  adminUnits: "ri-truck-line",
  adminGeofences: "ri-map-pin-5-line",
  adminDrivers: "ri-user-3-line",
  adminTrips: "ri-suitcase-line",
  shipmentsOpen: "ri-inbox-unarchive-line",
  shipmentsTrip: "ri-truck-line",
  unitDevices: "ri-cpu-line",
  unitDrivers: "ri-user-3-line",
  platformUsers: "ri-group-line",
  platformSessions: "ri-shield-user-line",
};

export interface ChatContext {
  id: ChatContextId;
  prefix: string;
  icon: string;
  labelKey: string;
}

// Matched by prefix, longest first: `/admin/units` has to win over `/admin`, and
// `/` matches everything, so it can only ever be last.
export const CHAT_CONTEXTS: ChatContext[] = [
  {
    id: "unidades",
    prefix: "/admin/units",
    icon: "ri-truck-line",
    labelKey: "monitoreoIa.chat.contexts.unidades",
  },
  {
    id: "monitoreo",
    prefix: "/monitoreo-ia",
    icon: "ri-shield-check-line",
    labelKey: "monitoreoIa.chat.contexts.monitoreo",
  },
  {
    id: "mapa",
    prefix: "/map",
    icon: "ri-map-pin-line",
    labelKey: "monitoreoIa.chat.contexts.mapa",
  },
  {
    id: "reportes",
    prefix: "/reports",
    icon: "ri-file-chart-line",
    labelKey: "monitoreoIa.chat.contexts.reportes",
  },
  {
    id: "envios",
    prefix: "/envios",
    icon: "ri-truck-line",
    labelKey: "monitoreoIa.chat.contexts.envios",
  },
  {
    id: "plataforma",
    prefix: "/chat-ia",
    icon: "ri-apps-2-line",
    labelKey: "monitoreoIa.chat.contexts.plataforma",
  },
  {
    id: "admin",
    prefix: "/admin",
    icon: "ri-settings-3-line",
    labelKey: "monitoreoIa.chat.contexts.admin",
  },
  { id: "general", prefix: "/", icon: "ri-apps-2-line", labelKey: "monitoreoIa.chat.contexts.general" },
];

export const CHAT_CONTEXT_SUGGESTIONS: Record<ChatContext["id"], readonly string[]> = {
  monitoreo: [
    "stopped",
    "longStop",
    "nightDriving",
    "engineOff",
    "offRoute",
    "lowFuel",
    "activeAlerts",
    "offline",
    "speeding",
  ],
  mapa: ["mapFocus", "mapRoutes", "mapRisk", "mapFuel"],
  reportes: ["reportDaily", "reportCalls", "reportKm", "reportAlerts"],
  envios: ["shipmentsOpen", "shipmentsTrip", "mapRoutes", "adminTrips"],
  unidades: ["adminUnits", "unitDevices", "unitDrivers", "offline"],
  admin: ["adminUnits", "adminGeofences", "adminDrivers", "adminTrips"],
  plataforma: ["summary", "platformUsers", "adminUnits", "reportDaily"],
  general: ["summary", "stopped", "mapRoutes", "reportDaily"],
};

// Context detection keywords for auto-switching
export const CONTEXT_DETECTION_KEYWORDS: Record<ChatContext["id"], string[]> = {
  unidades: [
    "unidad",
    "unidades",
    "vehícul",
    "auto",
    "camión",
    "truck",
    "vehicle",
    "dispositivo",
    "device",
    "conductor",
    "driver",
    "placa",
    "vin",
  ],
  monitoreo: [
    "monitoreo",
    "monitorear",
    "detenid",
    "ralentí",
    "velocidad",
    "speed",
    "alerta",
    "alert",
    "incidente",
    "incident",
    "conducción",
    "driving",
  ],
  mapa: [
    "mapa",
    "map",
    "ubicación",
    "location",
    "posición",
    "position",
    "ruta",
    "route",
    "trayecto",
    "track",
    "geolocalización",
    "geolocation",
  ],
  reportes: [
    "reporte",
    "report",
    "reportes",
    "histórico",
    "history",
    "estadística",
    "statistic",
    "estadísticas",
    "statistics",
    "dato",
    "data",
    "análisis",
    "analysis",
    "pdf",
    "excel",
    "exportar",
    "export",
    "documento",
    "document",
    "resumen",
    "summary",
    "diario",
    "daily",
  ],
  envios: [
    "envío",
    "shipment",
    "envíos",
    "pedido",
    "order",
    "entrega",
    "delivery",
    "paquete",
    "package",
    "remitente",
    "destinatario",
  ],
  admin: [
    "administración",
    "admin",
    "administrador",
    "usuario",
    "user",
    "permisos",
    "permiso",
    "configuración",
    "settings",
    "geocerca",
    "geofence",
  ],
  plataforma: [
    "plataforma",
    "platform",
    "sistema",
    "system",
    "aplicación",
    "app",
    "usuario",
    "usuario",
    "cuenta",
    "account",
  ],
  general: [],
};
